import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import {
  AUTH_TOKEN,
  REFRESH_TOKEN,
  AUTH_EXPIRES_IN,
  USERNAME,
  USER_EMAIL,
} from "constants/AuthConstant";
import { notification } from "antd";

const unauthorizedCode = [401, 403];

/** Ngrok free tier: skip browser warning page so APIs return JSON instead of HTML. */
const NGROK_SKIP_HEADER = "ngrok-skip-browser-warning";
const NGROK_SKIP_VALUE = "69420";

const shouldSendNgrokHeader = () =>
  typeof API_BASE_URL === "string" && API_BASE_URL.includes("ngrok");

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const TOKEN_PAYLOAD_KEY = "authorization";

service.interceptors.request.use(
  async (config) => {
    let jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
    const expiryTime = localStorage.getItem(AUTH_EXPIRES_IN) || 0;

    // Check if the token is about to expire (e.g., 1 minute before expiry)
    const isTokenExpired =
      expiryTime && new Date().getTime() > expiryTime - 60000;

    if (isTokenExpired && localStorage.getItem(REFRESH_TOKEN)) {
      jwtToken = await refreshAccessToken();
    }

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    if (shouldSendNgrokHeader()) {
      config.headers[NGROK_SKIP_HEADER] = NGROK_SKIP_VALUE;
    }

    return config;
  },
  (error) => {
    notification.error({ message: "Error" });
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Check if this is an endpoint that handles its own error messages in the component
    const requestUrl = error.config?.url || '';
    const isOutboundCallSummary = requestUrl.includes('outbound-call-summary');
    const isQuickDialPreviewCamp = requestUrl.includes('quickDialPreviewCamp');

    let notificationParam = { message: "" };
    if (error.response && error.response.data) {
      // Handle error response data - could be string, object with message, or object with detail
      if (typeof error.response.data === 'string') {
        notificationParam.message = error.response.data;
      } else if (error.response.data.message) {
        notificationParam.message = error.response.data.message;
      } else if (error.response.data.detail) {
        notificationParam.message = error.response.data.detail;
      } else if (error.response.data.status) {
        // e.g. { status: "{\"status\":\"No Agent Available...\"}" }
        try {
          const s = error.response.data.status;
          const parsed = typeof s === 'string' ? JSON.parse(s) : s;
          notificationParam.message = parsed?.status ?? s;
        } catch {
          notificationParam.message = String(error.response.data.status);
        }
      } else {
        notificationParam.message = JSON.stringify(error.response.data);
      }
    } else {
      notificationParam.message = "Something went wrong";
    }

    // Skip error notification for endpoints that show their own message in the component
    if (!isOutboundCallSummary && !isQuickDialPreviewCamp) {
      notification.error(notificationParam);
    }

    // Handle specific status codes
    if (error.response && unauthorizedCode.includes(error.response.status)) {
      notificationParam.message = "Authentication Fail";
      notificationParam.description = "Please login again";
      localStorage.removeItem(AUTH_TOKEN);
      store.dispatch(signOutSuccess());
    }

    return Promise.reject(error);
  }
);
// (error) => {
//   let notificationParam = { message: "" };
  
//   // ✅ FIXED: Proper null checking with correct logical operators
//   if (error.response && error.response.data) {
//     notificationParam.message = error.response.data.message || error.response.data;
//   } else if (error.request) {
//     // Request was made but no response received
//     notificationParam.message = "No response from server. Please check your connection.";
//   } else {
//     // Something else happened
//     notificationParam.message = error.message || "Something went wrong";
//   }
  
//   notification.error(notificationParam);

//   // Handle specific status codes - with null check
//   if (error.response && unauthorizedCode.includes(error.response.status)) {
//     notificationParam.message = "Authentication Fail";
//     notificationParam.description = "Please login again";
//     localStorage.removeItem(AUTH_TOKEN);
//     store.dispatch(signOutSuccess());
//   }

//   return Promise.reject(error);
// });

const refreshAccessToken = async () => {
  try {
    if (window.refreshTokenTimer) {
      clearTimeout(window.refreshTokenTimer);
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (refreshToken) {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refreshtoken`,
        {
          refreshToken: refreshToken,
        },
        shouldSendNgrokHeader()
          ? { headers: { [NGROK_SKIP_HEADER]: NGROK_SKIP_VALUE } }
          : undefined
      );

      const newAccessToken = response.data.accessToken;

      localStorage.setItem(AUTH_TOKEN, String(newAccessToken));
      localStorage.setItem(REFRESH_TOKEN, String(response.data.refreshToken));
      localStorage.setItem(AUTH_EXPIRES_IN, response.data.tokenExpiry);
      const tokenExpirySeconds =
        (response.tokenExpiry - new Date().getTime()) / 1000 - 60;
      
      // setRefreshTokenTimer(tokenExpirySeconds);
      // setRefreshTokenTimer(180);
      return newAccessToken;
    }
  } catch (error) {
    console.error("Error refreshing token: ", error);
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(USER_EMAIL);
    localStorage.removeItem(USERNAME);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(AUTH_EXPIRES_IN);
    window.location.reload();
  }
};

const setRefreshTokenTimer = (expiresIn) => {
  if (window.refreshTokenTimer) {
    clearTimeout(window.refreshTokenTimer);
  }
  const refreshTime = (expiresIn - 300) * 1000; // 5 minutes before expiry
  if (refreshTime > 0) {
    window.refreshTokenTimer = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  } else {
    // If the token is already expired or about to expire, refresh immediately
    refreshAccessToken();
  }
};

const initializeTokenRefresh = () => {
  const expiryTime = localStorage.getItem(AUTH_EXPIRES_IN);
  const currentTime = new Date().getTime();
  const isTokenExpired = expiryTime && currentTime >= expiryTime;
  if (!isTokenExpired) {
    const timeToExpiration = expiryTime - currentTime;
    setRefreshTokenTimer(timeToExpiration / 1000);
  }
  else {
    refreshAccessToken();
  }
};

initializeTokenRefresh();

// Download Excel function (unchanged)
const handleDownloadExcel = async (apiUrl, fileName) => {
  try {
    const response = await service({
      method: "GET",
      url: apiUrl,
      responseType: "blob",
    });
    console.log("contact data "+JSON.stringify(response))
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("File downloaded successfully");
  } catch (error) {
    console.error(
      "Error response data:",
      error.response ? error.response.data : "No response data"
    );
    console.error("Error downloading sample CSV:", error);
  }
};

export default service;
export { handleDownloadExcel, setRefreshTokenTimer };
